import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { fetchConfig } from '@theme/utilities';

/**
 * Favorieten: een lijst producthandles in de browser van de bezoeker.
 *
 * Er is bewust geen opslag per klant: favorieten werken zo ook zonder
 * account, maar gaan niet mee naar een ander apparaat. De producten zelf
 * worden pas getoond als de drawer opengaat, via de Section Rendering API
 * (sections/favorite-item.liquid op de productpagina-URL), zodat prijs en
 * voorraad altijd actueel zijn.
 */
const STORAGE_KEY = 'triova:favorieten';
const CHANGE_EVENT = 'wishlist:change';

/** @returns {string[]} */
function read() {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(value) ? value.filter((handle) => typeof handle === 'string') : [];
  } catch {
    return [];
  }
}

/** @param {string[]} handles */
function write(handles) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
  } catch {
    // Privévenster of volle opslag: de lijst werkt dan alleen deze pagina.
  }
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { handles } }));
}

export const wishlist = {
  /** @returns {string[]} Nieuwste eerst. */
  get handles() {
    return read();
  },

  /** @param {string} handle */
  has(handle) {
    return read().includes(handle);
  },

  /** @param {string} handle */
  add(handle) {
    const handles = read().filter((item) => item !== handle);
    write([handle, ...handles]);
  },

  /** @param {string} handle */
  remove(handle) {
    write(read().filter((item) => item !== handle));
  },

  /** @param {string} handle @returns {boolean} De nieuwe staat. */
  toggle(handle) {
    if (this.has(handle)) {
      this.remove(handle);
      return false;
    }
    this.add(handle);
    return true;
  },

  /** @param {(handles: string[]) => void} callback @returns {() => void} Afmelden. */
  subscribe(callback) {
    const onChange = () => callback(read());
    /** @param {StorageEvent} event */
    const onStorage = (event) => {
      if (event.key === STORAGE_KEY) onChange();
    };
    document.addEventListener(CHANGE_EVENT, onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      document.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener('storage', onStorage);
    };
  },
};

/**
 * Voegt varianten toe aan de winkelwagen en meldt dat via het standaard
 * cart-event, net als Horizon's product-form: de winkelwagen her-rendert en
 * de drawer gaat open (auto-open).
 *
 * @param {HTMLElement} source - Het element dat het event verstuurt.
 * @param {number[]} variantIds
 */
async function addToCart(source, variantIds) {
  if (!variantIds.length) return;

  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
    .filter(Boolean);

  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: variantIds.map((id) => ({ merchandiseId: String(id), quantity: 1 })),
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchConfig('json', {
        body: JSON.stringify({
          items: variantIds.map((id) => ({ id, quantity: 1 })),
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      }),
    });
    const result = await response.json();
    const cart = await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());

    if (result.status) {
      source.dispatchEvent(new CartErrorEvent({ error: result.message || 'Add to cart failed', code: 'INVALID' }));
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'favorites-drawer',
        sections: result.sections,
        didError: Boolean(result.status),
      },
    });
  } catch (error) {
    deferred.reject(error);
  }
}

/**
 * Het aantal favorieten als bolletje (op het hartje in de header).
 */
class WishlistCount extends HTMLElement {
  /** @type {(() => void) | undefined} */
  #unsubscribe;

  connectedCallback() {
    this.#render(wishlist.handles);
    this.#unsubscribe = wishlist.subscribe((handles) => this.#render(handles));
  }

  disconnectedCallback() {
    this.#unsubscribe?.();
  }

  /** @param {string[]} handles */
  #render(handles) {
    this.textContent = handles.length ? String(handles.length) : '';
    this.hidden = handles.length === 0;
  }
}

/**
 * Het hartje op de productpagina: zet het product in of uit de favorieten.
 *
 * @typedef {object} ButtonRefs
 * @property {HTMLButtonElement} button
 *
 * @extends {Component<ButtonRefs>}
 */
class WishlistButton extends Component {
  requiredRefs = ['button'];

  /** @type {(() => void) | undefined} */
  #unsubscribe;

  connectedCallback() {
    super.connectedCallback();
    this.#render();
    this.#unsubscribe = wishlist.subscribe(() => this.#render());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unsubscribe?.();
  }

  get handle() {
    return this.dataset.handle || '';
  }

  toggle() {
    if (!this.handle) return;
    wishlist.toggle(this.handle);
  }

  #render() {
    const active = wishlist.has(this.handle);
    this.toggleAttribute('active', active);
    this.refs.button.setAttribute('aria-pressed', String(active));
  }
}

/**
 * De inhoud van de favorieten-drawer. Haalt bij het openen elk product op als
 * kant-en-klare rij en toont anders de lege staat.
 *
 * @typedef {object} DrawerRefs
 * @property {HTMLElement} list - Hier komen de productrijen.
 * @property {HTMLElement} count - Het pilletje naast de titel.
 *
 * @extends {Component<DrawerRefs>}
 */
class FavoritesDrawerContent extends Component {
  requiredRefs = ['list', 'count'];

  /** @type {(() => void) | undefined} */
  #unsubscribe;

  /** @type {Map<string, Element>} Gerenderde rij per handle, voor deze pagina. */
  #rows = new Map();

  #rendering = Promise.resolve();

  connectedCallback() {
    super.connectedCallback();
    this.#unsubscribe = wishlist.subscribe(() => this.#queueRender());
    this.closest('theme-drawer')?.addEventListener(DrawerOpenEvent.eventName, this.#onOpen);
    this.#renderState(wishlist.handles.length);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unsubscribe?.();
    this.closest('theme-drawer')?.removeEventListener(DrawerOpenEvent.eventName, this.#onOpen);
  }

  #onOpen = () => {
    // Prijzen en voorraad kunnen sinds de vorige keer veranderd zijn.
    this.#rows.clear();
    this.#queueRender();
  };

  #queueRender() {
    this.#rendering = this.#rendering.then(() => this.#render()).catch(() => {});
  }

  async #render() {
    const handles = wishlist.handles;
    this.#renderState(handles.length);
    if (!handles.length) {
      this.refs.list.replaceChildren();
      return;
    }

    const rows = await Promise.all(handles.map((handle) => this.#fetchRow(handle)));
    const missing = handles.filter((_, index) => rows[index] === null);
    // Product bestaat niet meer (of is niet meer te koop in dit kanaal).
    if (missing.length) {
      missing.forEach((handle) => wishlist.remove(handle));
      return;
    }

    this.refs.list.replaceChildren(...rows.map((row) => /** @type {Element} */ (row).cloneNode(true)));
  }

  /** @param {string} handle @returns {Promise<Element | null>} */
  async #fetchRow(handle) {
    const cached = this.#rows.get(handle);
    if (cached) return cached;

    const url = `${Theme.routes.root.replace(/\/$/, '')}/products/${encodeURIComponent(handle)}?section_id=favorite-item`;
    const response = await fetch(url);
    if (response.status === 404) return null;
    if (!response.ok) throw new Error(`Favoriet ${handle}: ${response.status}`);

    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const row = html.querySelector('.favorite-item');
    if (!row) return null;

    this.#rows.set(handle, row);
    return row;
  }

  /** @param {number} count */
  #renderState(count) {
    this.toggleAttribute('data-empty', count === 0);
    const { count: pill } = this.refs;
    pill.hidden = count === 0;
    pill.textContent = (count === 1 ? this.dataset.countOne : this.dataset.countOther)?.replace('[count]', String(count)) ?? '';
  }

  /** @param {Event} event */
  remove(event) {
    const row = /** @type {HTMLElement | null} */ (/** @type {Element} */ (event.target).closest('.favorite-item'));
    const handle = row?.dataset.handle;
    if (handle) wishlist.remove(handle);
  }

  /** @param {Event} event */
  async addOne(event) {
    const row = /** @type {HTMLElement | null} */ (/** @type {Element} */ (event.target).closest('.favorite-item'));
    const variantId = Number(row?.dataset.variantId);
    if (!variantId || !row) return;
    await this.#closeAndAdd(row, [variantId]);
  }

  async addAll() {
    const ids = Array.from(this.refs.list.querySelectorAll('.favorite-item[data-variant-id]'))
      .map((row) => Number(/** @type {HTMLElement} */ (row).dataset.variantId))
      .filter(Boolean);
    await this.#closeAndAdd(this, ids);
  }

  /**
   * @param {HTMLElement} source
   * @param {number[]} variantIds
   */
  async #closeAndAdd(source, variantIds) {
    const drawer = /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
    await drawer?.close();
    await addToCart(source, variantIds);
  }
}

if (!customElements.get('wishlist-count')) customElements.define('wishlist-count', WishlistCount);
if (!customElements.get('wishlist-button-component')) customElements.define('wishlist-button-component', WishlistButton);
if (!customElements.get('favorites-drawer-component')) {
  customElements.define('favorites-drawer-component', FavoritesDrawerContent);
}
